import React, { useState } from "react";
import { Box, Tab, Tabs } from "@mui/material";

import analytics from "fond/analytics";
import { useFeatureFlag } from "fond/featureFlags";
import { Attribute, LayerId, Project, Version } from "fond/types";
import { Actions, permissionCheck } from "fond/utils/permissions";
import { BlockSpinner } from "fond/widgets";

import QuickComment from "../comments/QuickComment";

import FeatureAttachments from "./FeatureAttachments";
import FeaturePropertiesTable from "./FeaturePropertiesTable";

export interface IProps {
  project: Project;
  layerPropertiesSchema?: Attribute[];
  addNewComment: boolean;
  hideQuickComment(): void;
  editableProperties: string[];
  layerId: LayerId;
  version?: Version;
  featureProperties: Record<string, string | number>;
  architectureAddressTypes: string[] | null;
  setFieldValues(updates: Record<string, string | number>): void;
  lengthFields: string[];
  isFetching: boolean;
}

const PropertiesPanelContent: React.FC<IProps> = ({ project, layerPropertiesSchema, addNewComment, hideQuickComment, ...propertiesTableProps }) => {
  const [activeTab, setActiveTab] = useState<"properties" | "attachments">("properties");
  const { value: featureAttachmentsEnabled } = useFeatureFlag("feature_attachments", false);
  const canViewAttachments = permissionCheck(project.Permission.Level, Actions.FEATURE_VIEW_ATTACHMENT);

  const handleTabChange = (_: React.SyntheticEvent, newValue: "properties" | "attachments") => {
    setActiveTab(newValue);
    analytics.track("properties-panel.tab.clicked", { tab: newValue, projectId: project.ID });
  };
  if (!layerPropertiesSchema) return <BlockSpinner />;

  if (addNewComment) return <QuickComment onCancel={hideQuickComment} onSubmit={hideQuickComment} />;

  return (
    <Box overflow="hidden" flex={1} display="flex" flexDirection="column">
      <Box borderBottom={1} borderColor="divider">
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mx: 0.5 }}>
          <Tab label="Properties" value="properties" />
          {featureAttachmentsEnabled && canViewAttachments && <Tab label="Attachments" value="attachments" />}
        </Tabs>
      </Box>
      {activeTab === "properties" && (
        <FeaturePropertiesTable {...propertiesTableProps} project={project} layerPropertiesSchema={layerPropertiesSchema} />
      )}
      {activeTab === "attachments" && featureAttachmentsEnabled && <FeatureAttachments project={project} />}
    </Box>
  );
};

export default PropertiesPanelContent;
